import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ErrorBoundary from '../ErrorBoundary';
import { NySokContainer } from '../NySokContainer';
import { SourceReference } from '../common/SourceReference';
import { useKundmarkeContext } from '../common/contexts/KundmarkeContext';
import { usePersistedState } from '../common/hooks/usePersistedState';
import { removeStorageKey } from '../common/lib/storage';
import { hashCode } from '../common/miscFunctions';
import { KartstodModel } from '../kartstod/model/KartstodModel';
import { LoadState } from '../types/LoadState';
import { AngeFastigheter } from './AngeFastigheter';
import { Confirm } from './Confirm';
import { createForteckning } from './ForteckningarClient';
import { ForteckningarListContainer } from './ForteckningarListContainer';
import { ForteckningMetadataDto } from './types/dtos/ForteckningDto';

interface FastighetRow {
  id: number;
  fastighet: string;
}
export const fastigheterStorageKey = 'fastigheter';

export const createFastighet = (fastighet: string): FastighetRow => ({
  id: hashCode(fastighet),
  fastighet,
});

export const Forteckningar: React.FC = () => {
  const { kundmarke } = useKundmarkeContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [fastigheter, setFastigheter] = usePersistedState<KartstodModel[]>(
    fastigheterStorageKey,
    []
  );
  const [selectedTransaktionId, setSelectedTransaktionId] = useState<string>();
  const [createdForteckning, setCreatedForteckning] =
    useState<ForteckningMetadataDto>();
  const [registerbeteckningar, setRegisterbeteckningar] = useState<string[]>(
    fastigheter?.map((x) => x.fastighet) || []
  );
  const [tmpRegisterbeteckningar, setTmpRegisterbeteckningar] = useState<
    string[]
  >([]);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [createLoadState, setCreateLoadState] = useState(LoadState.Idle);
  const [registerbeteckningarPage, setRegisterbeteckningarPage] = useState(0);

  useEffect(() => {
    if (!tmpRegisterbeteckningar.length) {
      return setShowReplaceDialog(false);
    }
    if (!registerbeteckningar.length) {
      setRegisterbeteckningar(tmpRegisterbeteckningar);
      return setTmpRegisterbeteckningar([]);
    }
    setShowReplaceDialog(true);
  }, [tmpRegisterbeteckningar]);

  const skapaForteckning = () => {
    setShowConfirm(true);
  };

  const bekraftaSkapaForteckning = async () => {
    setShowConfirm(false);

    setCreateLoadState(LoadState.Loading);
    const response = await createForteckning(
      selectedTransaktionId,
      {
        fastigheter: registerbeteckningar,
      },
      kundmarke
    );

    setFastigheter([]);
    setRegisterbeteckningar([]);

    if (response.success) {
      setCreateLoadState(LoadState.Loading);
      setCreatedForteckning(response.data);
      return;
    }
    setCreateLoadState(LoadState.Error);
  };

  const handleLaggTill = () => {
    setRegisterbeteckningar(
      Array.from(new Set([...registerbeteckningar, ...tmpRegisterbeteckningar]))
    );
    setShowReplaceDialog(false);
  };

  const handleErsatt = () => {
    setRegisterbeteckningar([...tmpRegisterbeteckningar]);
    setRegisterbeteckningarPage(0);
    setShowReplaceDialog(false);
  };

  const getDuplicates = (list: string[]) =>
    Array.from(
      new Set(list.filter((item, index) => list.indexOf(item) !== index))
    );

  const duplicates = getDuplicates([
    ...registerbeteckningar,
    ...tmpRegisterbeteckningar,
  ]);

  return (
    <ErrorBoundary onError={() => removeStorageKey(fastigheterStorageKey)}>
      <NySokContainer heading={'Förteckningar'}>
        <AngeFastigheter
          skapaForteckning={skapaForteckning}
          selectedTransaktionId={selectedTransaktionId}
          setSelectedTransaktionId={setSelectedTransaktionId}
          registerbeteckningar={registerbeteckningar}
          setTmpRegisterbeteckningar={setTmpRegisterbeteckningar}
          setRegisterbeteckningar={setRegisterbeteckningar}
          registerbeteckningarPageState={[
            registerbeteckningarPage,
            setRegisterbeteckningarPage,
          ]}
        />
        <SourceReference
          sourceName="Lantmäteriet"
          style={{
            marginBottom: '-16px',
          }}
        />
        <Dialog open={showReplaceDialog}>
          <DialogTitle>Ersätt redan valda fastigheter?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <div>
                  Du har redan valt {registerbeteckningar.length} fastigheter.
                  Vill du ersätta fastigheterna med fastigheterna från filen?
                </div>
                {!!duplicates.length && (
                  <div>
                    Innehållet som valts innehåller {duplicates.length}{' '}
                    duplikat. Dessa kommer endast att inkluderas en gång:
                    <div>
                      <ul>
                        {duplicates.map((duplicate) => (
                          <li>{duplicate}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLaggTill}>Lägg till</Button>
            <Button onClick={handleErsatt} autoFocus>
              Ersätt
            </Button>
          </DialogActions>
        </Dialog>
        <Confirm
          antalFastigheter={registerbeteckningar.length}
          onCancel={() => setShowConfirm(false)}
          onConfirm={bekraftaSkapaForteckning}
          show={showConfirm}
          forteckningType={selectedTransaktionId}
        />
        {createLoadState === LoadState.Error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Något gick fel när förteckningen skapades.
          </Alert>
        )}
      </NySokContainer>
      <ForteckningarListContainer createdId={createdForteckning?.id} />
    </ErrorBoundary>
  );
};
